import { InfoCircleBigIcon } from '@app/components/icons/info-circle-big';
import { FLAG_RC_ANALYTICS_IFRAME } from '@app/feature-flags/flags';
import { useAnalytics } from '@app/features/analytics/hooks/use-analytics';
import { useFlags } from '@app/hooks/use-flags';
import { useTranslation } from '@hopin-team/provider-translation';
import cx from 'classnames';
import React from 'react';
import { useSelector } from 'react-redux';

import { Tooltip } from '../../components';
import {
  FLAG_ENABLE_ANALYTICS_LEAD_FILTER,
  FLAG_ENABLE_CH_DASHBOARD_ANALYTICS,
  FLAG_ENABLE_CSV_REPORTS_IMPROVEMENTS,
  FLAG_ENABLE_VIDEO_ANALYTICS_EXPORT,
} from '../../constants';
import { useAnalyticsContext } from '../../providers/analytics-provider';
import { selectAnalyticsSeriesInfo } from '../../selectors';
import { AllowedEvents } from '../../types';
import { ContactTypeFilter } from './contact-type-filter';
import { DateFilter } from './date-filter';
import { EventFilter } from './event-filter';
import { Export } from './export';
import { getAnalyticsTabs, resolveActiveTab } from './get-analytics-tabs';
import { SearchEvents } from './search-events';
import { SearchSeries } from './search-series';
import { TabGroupWrapper } from './tab-group';
import { VideoFilter } from './video-filter';

const eventMap: Record<string, AllowedEvents> = {
  admin: 'ola_admin_tab_click',
  overview: 'ola_overview_tab_click',
  events: 'ola_events_tab_click',
  series: 'ola_series_tab_click',
  registrants: 'ola_registrants_tab_click',
  videos: 'ola_video_tab_click',
};

type TopBarProps = {
  layout?: 'default' | 'embedded';
};

export const TopBar = ({ layout = 'default' }: TopBarProps) => {
  const { t } = useTranslation();
  const {
    state: { selectedTab },
    dispatch,
  } = useAnalyticsContext();
  const { track } = useAnalytics();
  const isEmbedded = layout === 'embedded';
  const flags = useFlags();
  const { filters } = useSelector(selectAnalyticsSeriesInfo);
  const enableLeadFilter = flags?.[FLAG_ENABLE_ANALYTICS_LEAD_FILTER];
  const enableVideoAnalyticsReport =
    flags?.[FLAG_ENABLE_VIDEO_ANALYTICS_EXPORT];
  const enableCHDashboardAnalytics =
    flags?.[FLAG_ENABLE_CH_DASHBOARD_ANALYTICS];
  const enableCsvReportsImprovements =
    flags?.[FLAG_ENABLE_CSV_REPORTS_IMPROVEMENTS];
  const showAdminPanel = flags?.[FLAG_RC_ANALYTICS_IFRAME];

  // Derive the validated active tab so toolbar decisions are always aligned
  // with what is actually rendered, even on the first frame after a stale
  // cookie value is still being healed by the normalization effect in
  // TabGroupWrapper.
  const tabs = getAnalyticsTabs({ enableCHDashboardAnalytics, showAdminPanel });
  const { activeTab } = resolveActiveTab(tabs, selectedTab);

  const setSelectedIndex = (index: number, tabName: string) => {
    dispatch({
      type: 'analytics tab updated',
      payload: { index: index, tabName },
    });

    const eventName = eventMap[tabName];
    if (eventName) {
      if (tabName === 'admin') {
        track(eventName);
        return;
      }

      track(eventName, { index, tabName: tabName.toLowerCase() });
    }
  };

  const getInfoTooltip = () => {
    if (enableCHDashboardAnalytics && activeTab === 'registrants') {
      return t('analytics.contacts-tooltip');
    }
    if (activeTab === 'series') {
      return t('analytics.series-tooltip');
    }
    return t('analytics.overview-tooltip');
  };

  const isExportable =
    activeTab === 'events' ||
    activeTab === 'registrants' ||
    activeTab === 'series' ||
    (activeTab === 'videos' && enableVideoAnalyticsReport) ||
    activeTab === 'admin';

  return (
    <div
      className={cx(
        'sticky left-0 right-0 z-[100] bg-white border-b border-gray-200 py-4 flex items-center justify-between',
        isEmbedded ? 'top-0' : 'top-16',
      )}
    >
      <div
        className={cx('mx-auto flex items-center justify-between w-full', {
          'max-w-[90rem]': !isEmbedded,
        })}
      >
        <TabGroupWrapper setSelectedIndex={setSelectedIndex} />
        <div className="flex: 1" />
        <div className="flex items-center gap-4">
          {activeTab !== 'admin' && (
            <Tooltip tooltip={getInfoTooltip()} placement="bottom-center">
              <InfoCircleBigIcon />
            </Tooltip>
          )}
          {isExportable && (
            <Export
              customProps={
                activeTab === 'series' ? { searchText: filters.searchText } : {}
              }
            />
          )}
          {activeTab !== 'overview' &&
            activeTab !== 'series' &&
            activeTab !== 'admin' && <SearchEvents />}
          {activeTab === 'series' && <SearchSeries />}

          {enableCsvReportsImprovements &&
            activeTab !== 'series' &&
            activeTab !== 'admin' &&
            activeTab !== 'videos' && <EventFilter />}
          {activeTab === 'registrants' && enableLeadFilter && (
            <ContactTypeFilter />
          )}
          {activeTab === 'registrants' && <VideoFilter />}
          {activeTab !== 'series' && <DateFilter />}
        </div>
      </div>
    </div>
  );
};
